import { useEffect, useState } from 'react';
import { Folder } from 'lucide-react';
import Modal from '../../../shared/Modal';
import ModalFormActions from '../../../shared/ModalFormActions';
import { CheckboxField, TextInput } from '../../../shared/FormControls';

/** Form state and payload match backend: name, code (required), description, isActive */
const initialFormState = {
  name: '',
  code: '',
  description: '',
  isActive: true,
};

const FolderTypeModal = ({
  isOpen,
  mode = 'create',
  folderType,
  onSubmit,
  onClose,
  isSubmitting = false,
  serverError,
}) => {
  const [formValues, setFormValues] = useState(initialFormState);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      setErrors({});

      if (mode === 'edit' && folderType) {
        setFormValues({
          name: folderType.name ?? '',
          code: folderType.code ?? '',
          description: folderType.description ?? '',
          isActive: Boolean(folderType.isActive),
        });
      } else {
        setFormValues(initialFormState);
      }
    }
  }, [isOpen, mode, folderType]);

  const validate = () => {
    const nextErrors = {};

    if (!formValues.name.trim()) {
      nextErrors.name = 'Folder type name is required';
    }
    if (!formValues.code.trim()) {
      nextErrors.code = 'Folder type code is required';
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const handleChange = (field) => (event) => {
    const value = field === 'isActive' ? event.target.checked : event.target.value;
    if (field === 'code') {
      setFormValues((previous) => ({ ...previous, [field]: value.toUpperCase() }));
    } else {
      setFormValues((previous) => ({ ...previous, [field]: value }));
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    const payload = {
      name: formValues.name.trim(),
      code: formValues.code.trim(),
      description: formValues.description?.trim() ?? '',
      isActive: formValues.isActive,
    };

    onSubmit({ mode, payload });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'edit' ? 'Edit Folder Type' : 'Create Folder Type'}
      size="lg"
      icon={<Folder className="h-6 w-6 text-primary-dark" strokeWidth={1.5} />}
      footer={
        <ModalFormActions
          onCancel={onClose}
          formId="folder-type-form"
          isSubmitting={isSubmitting}
          submitText={mode === 'edit' ? 'Save changes' : 'Create Folder Type'}
        />
      }
    >
      <form id="folder-type-form" className="space-y-4" onSubmit={handleSubmit}>
        {serverError ? (
          <p className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">
            {serverError}
          </p>
        ) : null}

        <div className="grid gap-4 md:grid-cols-1">
          <TextInput
            id="name"
            label="Folder Type Name"
            type="text"
            value={formValues.name}
            onChange={handleChange('name')}
            placeholder="e.g. Project Folder"
            maxLength={200}
            disabled={isSubmitting}
            error={errors.name}
          />

          <TextInput
            id="code"
            label="Folder Type Code"
            type="text"
            value={formValues.code}
            onChange={handleChange('code')}
            placeholder="e.g. PROJ_FOLDER"
            maxLength={50}
            disabled={isSubmitting || mode === 'edit'}
            error={errors.code}
          />

          <div className="space-y-1">
            <label htmlFor="description" className="text-sm font-medium text-primary-dark">
              Description
            </label>
            <textarea
              id="description"
              value={formValues.description}
              onChange={handleChange('description')}
              rows={3}
              className="mt-1 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-primary-dark focus:outline-none focus:ring-2 focus:ring-primary-dark/30"
              placeholder="Optional description"
              maxLength={1000}
              disabled={isSubmitting}
            />
          </div>

          {mode === 'edit' ? (
            <CheckboxField
              id="isActive"
              label="Folder type is active"
              checked={formValues.isActive}
              onChange={handleChange('isActive')}
              disabled={isSubmitting}
              wrapperClassName="pt-6"
            />
          ) : null}
        </div>
      </form>
    </Modal>
  );
};

export default FolderTypeModal;
